import React, { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Shield, Clock, TrendingUp, CheckCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

interface LoginPageProps {
  onLogin?: () => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
      if (onLogin) onLogin();
    } catch (err: any) {
      setError(err.message || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="w-2/5 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-white p-12 flex flex-col justify-center relative overflow-hidden">
        {/* Subtle pattern background */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)`,
            }}
          ></div>
        </div>

        <div className="relative z-10">
          {/* Logo */}
          <div className="mb-12">
            <div className="flex items-center space-x-3 mb-4">
              <div className="relative">
                <div className="w-12 h-12 bg-blue-500 transform -rotate-12 absolute"></div>
                <div className="w-12 h-12 bg-teal-500 transform rotate-12"></div>
              </div>
              <h1 className="text-4xl font-bold text-white">Infervia</h1>
            </div>
            <p className="text-xl text-blue-200 font-light">
              AI-Powered Regulatory Intelligence for Healthcare
            </p>
          </div>

          {/* Value Props */}
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-300" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">
                  Monitor 6 Federal Sources Automatically
                </h3>
                <p className="text-sm text-slate-300">
                  CMS, HHS, Federal Register - all in one platform
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-teal-300" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">
                  Get AI Analysis Within 5 Minutes
                </h3>
                <p className="text-sm text-slate-300">
                  Instant impact assessment for your facility
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-300" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">
                  Save 10-15 Hours Per Week
                </h3>
                <p className="text-sm text-slate-300">
                  Automated monitoring and intelligent filtering
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-teal-300" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">
                  95% Accuracy, 90% Noise Reduction
                </h3>
                <p className="text-sm text-slate-300">
                  Only see what matters to your organization
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Sign In</h2>
            <p className="text-slate-600">
              Access your regulatory intelligence platform
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2"
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Button variant="ghost" size="sm" className="text-sm">
              Forgot password?
            </Button>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center">
              Infervia provides regulatory information for professional
              evaluation only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
